import React from 'react';
import i18next from "i18next";

import { Chrono } from "react-chrono";

import { DataTypeDisplayValue } from "../DataTypeDisplayValue";
import { DeviceDataOverrideService, TimeFormattingService } from "../../services";

const AlertHistory = ({ alertHistory, timezone }) => (
    <div className="op-content-box">
        <div className="row">
            <div className="col-sm-12 col-lg-12 col-md-12">
                <h4 style={{ color: "#7F7F7F" }}>{i18next.t("pages.event-details.status-history")}</h4>
                <table className="table table-striped">
                    <thead>
                        <tr>
                            <th>{i18next.t("pages.event-details.previous-status")}</th>
                            <th>{i18next.t("pages.event-details.new-status")}</th>
                            <th>{i18next.t("pages.event-details.timestamp")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {alertHistory
                            .sort((entryA, entryB) => entryB.setAt - entryA.setAt)
                            .map((entry) => {
                                return (
                                    <tr key={entry.id}>
                                        <td>{entry.previousStatus ? i18next.t(`alert-statuses.${entry.previousStatus.token}`) : i18next.t("none")}</td>
                                        <td>{i18next.t(`alert-statuses.${entry.status.token}`)}</td>
                                        <td>{TimeFormattingService.renderNormalTime(entry.setAt, timezone ? timezone : null)}</td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </table>
            </div>
        </div>
    </div>
);

const NotificationHistory = ({ alertNotificationHistory, timezone }) => {
    return alertNotificationHistory && alertNotificationHistory.length > 0 ? (
        <div className="op-content-box">
            <div className="row">
                <div className="col-sm-12 col-lg-12 col-md-12">
                    <h4 style={{ color: "#7F7F7F" }}>{i18next.t("pages.event-details.notification-history")}</h4>
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>{i18next.t("pages.event-details.notification-sent-to")}</th>
                                <th>{i18next.t("pages.event-details.timestamp")}</th>
                                <th>{i18next.t("pages.event-details.notification-error")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {alertNotificationHistory
                                .sort((entryA, entryB) => entryB.sentAt - entryA.sentAt)
                                .map((entry) => {
                                    return (
                                        <tr key={entry.id}>
                                            <td>
                                                {i18next.t(`notification-type.${entry.typeToken}`)} to {entry.sentTo} {entry.recipientName ? `(${entry.recipientName})` : ""}
                                            </td>
                                            <td>{TimeFormattingService.renderNormalTime(entry.sentAt, timezone ? timezone : null)}</td>
                                            <td>{entry.failureMessage || "None"}</td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    ) : null;
};


const AlertFirstExceededTimelineEntryView = ({entry}) => {
  let data = entry;
  if (data?.numericValue != null) {
      data = { ...data, numericValue: DeviceDataOverrideService.getOverrideFunctionForDeviceTypeToken(data.deviceDataType.token)(data.numericValue) };
  }
  return (<DataTypeDisplayValue value={data} />);
}

const AlertMinDatapointTimelineEntryView = ({entry}) => {
  let data = entry;
  return (<DataTypeDisplayValue value={data} />);
}

const AlertMaxDatapointTimelineEntryView = ({entry}) => {
  let data = entry;
  return (<DataTypeDisplayValue value={data} />);
}

const AlertNotificationTimelineEntryView = ({entry}) => {
  return (<span>
    { entry.failureMessage ? (<b style={{color: 'red'}}> {entry.failureMessage} <br/> <br/> </b>) : <span/> }
    { i18next.t(`notification-type.${entry.typeToken}`) } to { entry.sentTo } {entry.recipientName ? `(${entry.recipientName})` : ''}
  </span>);
}

const AlertResponseTimelineEntryView = ({entry}) => {
  return (
    <div>
      {entry.note}
    </div>
  );
}

const AlertHistoryTimelineEntryView = ({entry}) => {
  if (entry.previousStatus == null) {
    return (<span>Initial status set to {i18next.t(`alert-statuses.${entry.status.token}`)}</span>)
  } else {
    return (<span>Status Changed from {i18next.t(`alert-statuses.${entry.previousStatus.token}`)} to {i18next.t(`alert-statuses.${entry.status.token}`)}</span>);
  }
}

const AlertGeneratedTimelineEntryView = ({entry}) => {
  return (<span>{i18next.t(`alert-levels.${entry.alertLevel.token}`)} alert generated</span>);
}

const TimelineView = ({alert, minDatapoint, maxDatapoint}) => {
  const timezone = alert.originatingGrouping.timezone;

  let timelineValues = [{ 
    time: alert.occurredAt,
    title: TimeFormattingService.renderNormalTime(alert.occurredAt, timezone ? timezone : null),
    cardTitle: 'Alert Created',
    timelineContent: <AlertGeneratedTimelineEntryView entry={alert} />
  }];

  if (alert.history) {
    alert.history.forEach(entry => {
      var byUser = 'System';
      if (entry.changedByUser?.firstName != null &&  entry.changedByUser?.lastName != null) {
        byUser = entry.changedByUser?.firstName + ' ' + entry.changedByUser?.lastName;
      }
      const titleTime = TimeFormattingService.renderNormalTime(entry.setAt, timezone ? timezone : null);
      timelineValues.push({ 
        time: entry.setAt, 
        title: <>{titleTime} <span>{byUser}</span></>,
        cardTitle: 'Status Change', 
        timelineContent: <AlertHistoryTimelineEntryView entry={entry} />
      });
    });
  }

  if (alert.alertNotificationHistory) {
    alert.alertNotificationHistory.forEach(entry => timelineValues.push({
      time: new Date(entry.sentAt) - 0, 
      title: TimeFormattingService.renderNormalTime(new Date(entry.sentAt) - 0, timezone ? timezone : null),
      cardTitle: entry.failureMessage ? 'Notification Error' : 'Notification Sent', 
      timelineContent: <AlertNotificationTimelineEntryView entry={entry} />
    }));
  }

  if (alert.responses) {
    alert.responses.forEach(entry => {
      var byUser = 'System';
      if (entry.user?.firstName != null &&  entry.user?.lastName != null) {
        byUser = entry.user?.firstName + ' ' + entry.user?.lastName;
      }
      const titleTime = TimeFormattingService.renderNormalTime(entry.createdAt, timezone ? timezone : null);
      timelineValues.push({
        time: entry.createdAt, 
        title: <>{titleTime} <span>{byUser}</span></>,
        cardTitle: i18next.t(`alert-response-types.${entry.responseType.token}`) + ' Added', 
        timelineContent: <AlertResponseTimelineEntryView entry={entry} />
      });
    });
  }

  if (minDatapoint) {
    timelineValues.push({
      time: minDatapoint.createdAt, 
      title: TimeFormattingService.renderNormalTime(minDatapoint.createdAt, timezone ? timezone : null),
      cardTitle: 'Minimum Value Reached', 
      timelineContent: <AlertMinDatapointTimelineEntryView entry={minDatapoint} />
    });
  }

  if (maxDatapoint) {
    timelineValues.push({
      time: maxDatapoint.createdAt, 
      title: TimeFormattingService.renderNormalTime(maxDatapoint.createdAt, timezone ? timezone : null),
      cardTitle: 'Maximum Value Reached', 
      timelineContent: <AlertMaxDatapointTimelineEntryView entry={maxDatapoint} />
    });
  }

  if (alert.thresholdExceededEvents && alert.thresholdExceededEvents.length > 0) {
    const entry = alert.thresholdExceededEvents[0].triggerData;
    if (entry) {
        timelineValues.push({
            time: entry.createdAt,
            title: TimeFormattingService.renderNormalTime(entry.createdAt, timezone ? timezone : null),
            cardTitle: "Threshold First Exceeded",
            timelineContent: <AlertFirstExceededTimelineEntryView entry={entry} />,
        });
    }
  }

  return (
    <div className="op-content-box">
      <h4 style={{color: "#7F7F7F"}}>{ i18next.t('pages.event-details.activity-log') }</h4>
      <div className="row">
        <div className="col-sm-12">
          <Chrono 
            className="timeline"
            items={timelineValues.sort((entryA, entryB) => entryB.time - entryA.time)} 
            mode="VERTICAL" 
            cardHeight={30}
            hideControls
            classNames={{
              card: 'timeline-card',
              cardSubTitle: 'timeline-card-subtitle',
              cardText: 'timeline-card-text',
              cardTitle: 'timeline-card-title',
              controls: 'timeline-controls',
              title: 'timeline-title',
            }}
          />
        </div>
      </div>
    </div>
  );
};

export {
    AlertHistory,
    NotificationHistory,
    TimelineView
};
